// Web Push subscription helper. The flow when a user enables notifications:
//   1. fetch the server's VAPID public key (503 → push not configured)
//   2. ask the browser for notification permission
//   3. subscribe via the service worker's PushManager
//   4. register the subscription with the backend
// The in-app feed works regardless — push is an additive layer, so every
// failure here degrades to "feed only" rather than blocking the toggle.

import { api } from './api';

export type PushResult = 'subscribed' | 'denied' | 'unsupported' | 'unconfigured' | 'error';

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

async function swRegistration(): Promise<ServiceWorkerRegistration | null> {
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) return existing;
  // Dev builds don't register the SW at boot (main.tsx gates on PROD) —
  // register on demand so push is testable locally too.
  try {
    return await navigator.serviceWorker.register(`${import.meta.env.BASE_URL}sw.js`);
  } catch {
    return null;
  }
}

/** Subscribe this browser to push and register it with the backend. */
export async function ensurePushSubscription(): Promise<PushResult> {
  if (!pushSupported()) return 'unsupported';

  let publicKey: string;
  try {
    publicKey = (await api<{ public_key: string }>('/me/push/vapid')).public_key;
  } catch {
    return 'unconfigured';
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return 'denied';

  try {
    const reg = await swRegistration();
    if (!reg) return 'error';
    const sub =
      (await reg.pushManager.getSubscription()) ??
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
      }));
    await api('/me/push/subscribe', { method: 'POST', body: sub.toJSON() });
    return 'subscribed';
  } catch {
    return 'error';
  }
}

/** Drop this browser's push subscription (backend row + browser side). */
export async function disablePushSubscription(): Promise<void> {
  if (!pushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    if (!sub) return;
    try {
      await api('/me/push/unsubscribe', { method: 'POST', body: { endpoint: sub.endpoint } });
    } catch {
      /* backend row may already be gone — still unsubscribe locally */
    }
    await sub.unsubscribe();
  } catch {
    /* no-op */
  }
}
